import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { FileSpreadsheet, FlaskConical, FolderPlus, Link2, Send } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, InfoHint, NativeSelect, Skeleton, useSave } from "@ncp/ui";
import { useRequests, type StudyFile } from "./api";

/**
 * The market-study file the study runs under: its number and the stage of the file's procedure (terms of reference, data collection, analysis,
 * consultation, drafting, the Director's or Under-Secretary's approval, publication). An authorised officer opens the file for a study that has
 * none – the requests already sent are filed under it – or ties the study to an existing file before its first request (condition 1.10), so
 * that on a test file the requests reach no one outside the Department.
 */
function StudyFileLine({ studyId, canTie }: { studyId: string; canTie: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const file = useQuery({ queryKey: ["requests", "study-file", studyId], queryFn: () => api.get<StudyFile>(`/api/requests/studies/${studyId}/file`) });
  const [choosing, setChoosing] = useState(false);
  const [caseId, setCaseId] = useState("");
  const files = useQuery({
    queryKey: ["cases", "market-study-files"],
    queryFn: () => api.get<{ items: Array<{ id: string; caseNumber: string; title: string; isTest?: boolean }> }>("/api/cases", { type: "MarketStudy", pageSize: "100" }),
    enabled: choosing,
  });
  const refresh = () => { for (const k of [["requests", "study-file", studyId], ["databank", "studies"], ["databank", "study", studyId]]) void qc.invalidateQueries({ queryKey: k }); };
  const tie = useSave({
    mutationFn: () => api.post<StudyFile>(`/api/requests/studies/${studyId}/file`, { caseId }),
    expect: "record",
    saved: t("backoffice:requests.study.fileTied"),
    onSuccess: () => { setChoosing(false); refresh(); },
  });
  const open = useSave({
    mutationFn: () => api.post<{ caseId: string; caseNumber: string }>(`/api/databank/studies/${studyId}/file/open`, {}),
    expect: "record",
    saved: (r) => t("backoffice:processes.study.fileOpened", { caseNumber: r.caseNumber }),
    onSuccess: refresh,
  });
  const f = file.data;
  if (!f) return null;
  if (f.caseId) {
    return (
      <p className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="study-file">
        {t("backoffice:requests.study.file")} <Link to={`/cases/${f.caseId}`} className="font-mono text-aeblack-900 hover:underline">{f.caseNumber}</Link>
        {f.stateLabel ? <Badge size="sm" variant="outline">{t("backoffice:processes.study.stage", { stage: locale === "ar" ? f.stateLabelAr ?? f.stateLabel : f.stateLabel })}</Badge> : null}
        {f.isTest ? <Badge size="sm" variant="camel"><FlaskConical /> {t("backoffice:requests.study.testFile")}</Badge> : null}
      </p>
    );
  }
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="study-file">
      <span>{t(!f.mayTie ? "backoffice:processes.study.noFileNotAuthorised" : canTie ? "backoffice:processes.study.noFile" : "backoffice:processes.study.noFileSent")}</span>
      {f.mayTie && !choosing ? <Button size="sm" variant="outline" loading={open.isPending} onClick={() => open.mutate()}><FolderPlus /> {t("backoffice:processes.study.openFile")}</Button> : null}
      {f.mayTie && canTie && !choosing ? <Button size="sm" variant="ghost" onClick={() => setChoosing(true)}><Link2 /> {t("backoffice:requests.study.tie")}</Button> : null}
      {choosing ? (
        <>
          <NativeSelect value={caseId} onChange={(e) => setCaseId(e.target.value)} className="h-8 w-72" aria-label={t("backoffice:requests.study.tie")}>
            <option value="">—</option>
            {(files.data?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.caseNumber} · {c.title}</option>)}
          </NativeSelect>
          <Button size="sm" variant="outline" disabled={!caseId} loading={tie.isPending} onClick={() => tie.mutate()}>{t("backoffice:requests.study.tieConfirm")}</Button>
          <Button size="sm" variant="ghost" onClick={() => setChoosing(false)}>{t("actions.cancel")}</Button>
        </>
      ) : null}
    </div>
  );
}
import { RequestTable } from "./RequestTable";
import { DataRequestDialog } from "./DataRequestDialog";
import { RequestReviewSheet } from "./RequestReviewSheet";
import { useProcedureTermWords } from "./terms";

/** The market study's data requests: who was asked, where each answer stands, and a new request to the market's undertakings. */
export function StudyDataRequests({ study, openRequestId, onRequestClosed }: {
  study: { id: string; title: string; titleAr?: string | null; marketId?: string | null; scope: string }; openRequestId?: string | null; onRequestClosed?: () => void;
}) {
  const { t } = useTranslation();
  // Conditions 2.13 and 2.14: the periods and provisions a request names, from their one definition and the legal rules register.
  const terms = useProcedureTermWords();
  const requests = useRequests({ studyId: study.id });
  const [composing, setComposing] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const shown = selected ?? openRequestId ?? null;
  const rows = requests.data ?? [];
  return (
    <section data-section="study-data-requests">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-base font-semibold text-aeblack-950"><FileSpreadsheet className="size-4 text-aegold-700" /> {t("backoffice:requests.study.title")}<InfoHint>{t("backoffice:requests.study.hint", terms)}</InfoHint></h4>
        <Button size="sm" variant="outline" onClick={() => setComposing(true)}><Send /> {t("backoffice:requests.study.new")}</Button>
      </div>
      {!requests.isLoading ? <StudyFileLine studyId={study.id} canTie={rows.length === 0} /> : null}
      {requests.isLoading ? <Skeleton className="h-20" /> : rows.length === 0 ? <p className="text-sm text-muted-foreground">{t("backoffice:requests.study.empty")}</p> : <RequestTable rows={rows} onOpen={setSelected} />}
      <DataRequestDialog open={composing} onOpenChange={setComposing} study={study} />
      <RequestReviewSheet id={shown} onClose={() => { setSelected(null); onRequestClosed?.(); }} />
    </section>
  );
}
